import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Edit, FileText, GitBranch, Plus, Tag, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { getUserDropdownKeywords, getUserDropdownLabel, getUserRegistrationNumber } from '@/lib/user-display'
import { orgTreeApi, type OrgTreeNode } from '@/services/org-tree'
import { orgUsersApi } from '@/services/org-users'

const OWN_BRANCH_VALUE = '1'
const VENDOR_BRANCH_VALUE = '2'

const withSearch = (pathname: string, params: URLSearchParams, updates: Record<string, string | null | undefined>) => {
  const next = new URLSearchParams(params)
  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      next.set(key, value)
      return
    }
    next.delete(key)
  })

  const query = next.toString()
  return query ? `${pathname}?${query}` : pathname
}

export default function TreeClientChildPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const parentServiceInstanceId = searchParams.get('id')
  const name = searchParams.get('name')
  const clientMode = searchParams.get('client') === 'client'
  const [selectedNode, setSelectedNode] = useState<OrgTreeNode | null>(null)
  const [branchName, setBranchName] = useState('')
  const [selectedVariant, setSelectedVariant] = useState('')
  const [branchOwnership, setBranchOwnership] = useState(OWN_BRANCH_VALUE)
  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const branchesQuery = useQuery({
    queryKey: ['org-tree-client-branches', id],
    queryFn: () => orgTreeApi.listChildren(id!, { serviceInstance: true }),
    enabled: Boolean(id),
  })

  const variantsQuery = useQuery({
    queryKey: ['org-tree-instance-variants', parentServiceInstanceId],
    queryFn: () => orgTreeApi.listInstanceVariants(parentServiceInstanceId!),
    enabled: Boolean(parentServiceInstanceId) && isCreateOpen,
  })

  const sourceInstanceQuery = useQuery({
    queryKey: ['org-tree-service-instance-source', parentServiceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstance(parentServiceInstanceId!),
    enabled: Boolean(parentServiceInstanceId) && isCreateOpen,
  })

  const ownerUsersQuery = useQuery({
    queryKey: ['branch-owner-users', parentServiceInstanceId],
    queryFn: () => orgUsersApi.list({ includeInactive: false, serviceInstanceId: parentServiceInstanceId, pageSize: 1000 }),
    enabled: Boolean(parentServiceInstanceId) && isCreateOpen,
    staleTime: 60_000,
  })

  const activeOwnerUsers = useMemo(
    () => (ownerUsersQuery.data ?? []).filter((user) => user.is_active !== false),
    [ownerUsersQuery.data],
  )

  const vendorOwnerUsers = useMemo(
    () => activeOwnerUsers.filter((user) => {
      const groupCode = String(user.user_group_code ?? '').trim().toLowerCase()
      const groupTitle = String(user.user_group_title ?? '').trim().toLowerCase()
      return groupCode.includes('vendor') || groupTitle.includes('vendor')
    }),
    [activeOwnerUsers],
  )

  const ownerCandidates = useMemo(
    () => (branchOwnership === VENDOR_BRANCH_VALUE ? vendorOwnerUsers : activeOwnerUsers),
    [activeOwnerUsers, branchOwnership, vendorOwnerUsers],
  )

  const ownerUserOptions = useMemo(() => {
    const options = ownerCandidates.map((user) => {
      return {
        value: String(user.id),
        label: getUserDropdownLabel(user, user.id),
        keywords: getUserDropdownKeywords(user),
      }
    })

    const sourceOwnerId = String(sourceInstanceQuery.data?.owner_user_id ?? '').trim()
    if (branchOwnership === OWN_BRANCH_VALUE && sourceOwnerId && !options.some((option) => option.value === sourceOwnerId)) {
      const sourceOwnerProfile = sourceInstanceQuery.data?.owner_profile
      const sourceOwnerName = String(sourceOwnerProfile?.name ?? sourceInstanceQuery.data?.owner_name ?? '').trim()
      const sourceOwnerRegistrationNumber = getUserRegistrationNumber(sourceOwnerProfile ?? sourceInstanceQuery.data)
      const sourceOwnerPhone = String(sourceOwnerProfile?.phone ?? sourceInstanceQuery.data?.owner_phone ?? '').trim()
      const detail = [sourceOwnerRegistrationNumber, sourceOwnerPhone].filter(Boolean).join(' • ')
      options.unshift({
        value: sourceOwnerId,
        label: detail ? `${sourceOwnerName || `User ${sourceOwnerId}`} • ${detail}` : sourceOwnerName || `User ${sourceOwnerId}`,
        keywords: [sourceOwnerName, sourceOwnerRegistrationNumber, sourceOwnerPhone].filter(Boolean).join(' '),
      })
    }

    return options
  }, [branchOwnership, ownerCandidates, sourceInstanceQuery.data])

  useEffect(() => {
    if (!isCreateOpen || branchOwnership !== OWN_BRANCH_VALUE || selectedOwnerUserId) {
      return
    }

    const ownerUserId = String(sourceInstanceQuery.data?.owner_user_id ?? '').trim()
    if (ownerUserId && ownerUserOptions.some((option) => option.value === ownerUserId)) {
      setSelectedOwnerUserId(ownerUserId)
    }
  }, [branchOwnership, isCreateOpen, ownerUserOptions, selectedOwnerUserId, sourceInstanceQuery.data?.owner_user_id])

  const createMutation = useMutation({
    mutationFn: () => orgTreeApi.createChildBranch({
      name: branchName.trim(),
      service_variant: selectedVariant,
      org_instance_id: parentServiceInstanceId!,
      branch_ownership: branchOwnership,
      owner_user_id: selectedOwnerUserId,
    }),
    onSuccess: async () => {
      toast.success('Branch created successfully')
      setIsCreateOpen(false)
      setBranchName('')
      setSelectedVariant('')
      setBranchOwnership(OWN_BRANCH_VALUE)
      setSelectedOwnerUserId('')
      await queryClient.invalidateQueries({ queryKey: ['org-tree-client-branches', id] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: () => orgTreeApi.updateNode(selectedNode!.id, { node_name: branchName.trim() }),
    onSuccess: async () => {
      toast.success('Branch updated successfully')
      setIsEditOpen(false)
      setSelectedNode(null)
      await queryClient.invalidateQueries({ queryKey: ['org-tree-client-branches', id] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: () => orgTreeApi.deleteNode(selectedNode!.id),
    onSuccess: async () => {
      toast.success('Branch deleted successfully')
      setIsDeleteOpen(false)
      setSelectedNode(null)
      await queryClient.invalidateQueries({ queryKey: ['org-tree-client-branches', id] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const backPath = clientMode
    ? `/org/node/subclient-add/${localStorage.getItem('node_id') || id}?client=client`
    : `/org/node/client-add/${localStorage.getItem('node_id') || id}`

  const sortedBranches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data])

  const handleCreate = () => {
    if (!branchName.trim()) {
      toast.error('Branch name is required')
      return
    }

    if (!selectedVariant) {
      toast.error('App type is required')
      return
    }

    if (!branchOwnership) {
      toast.error('Branch ownership is required')
      return
    }

    if (!selectedOwnerUserId) {
      toast.error('Branch owner is required')
      return
    }

    createMutation.mutate()
  }

  if (branchesQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 w-full rounded-[28px]" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-44 rounded-[28px]" />)}
        </div>
      </div>
    )
  }

  if (branchesQuery.error) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Failed to load vendor branches</p>
        <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(branchesQuery.error)}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(withServiceInstanceId(backPath, serviceInstanceId))}>Back</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{name || 'Store'} Branch List</h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                Vendor-specific branch flow from the legacy setup screens. Add, rename, delete, or open the basic details page for store branches.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" asChild>
                <Link to={withServiceInstanceId(backPath, serviceInstanceId)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Link>
              </Button>
              <Button onClick={() => setIsCreateOpen(true)} disabled={!parentServiceInstanceId}>
                <Plus className="mr-2 h-4 w-4" />
                Add Branch
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {sortedBranches.length === 0 ? (
          <Card className="rounded-[28px] border-dashed border-border/70 md:col-span-2 xl:col-span-3">
            <CardContent className="flex min-h-52 items-center justify-center text-center text-sm text-muted-foreground">
              No branches found for this store.
            </CardContent>
          </Card>
        ) : sortedBranches.map((branch) => {
          const branchInstanceId = branch.service_instance_data?.[0]?.instance_id || branch.service_instance_data?.[0]?.id || parentServiceInstanceId
          const basicDetailsPath = withSearch(`/org/Client-branch-details/${branch.id}`, searchParams, {
            service: 'client',
            parent_id: id,
            branchname: branch.node_name || null,
            name,
            serviceName: 'client',
            branchID: branchInstanceId ? String(branchInstanceId) : null,
          })

          return (
            <Card key={String(branch.id)} className="rounded-[28px] border-border/80">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GitBranch className="h-5 w-5 text-amber-700" />
                  {branch.node_name || `Branch ${branch.id}`}
                </CardTitle>
                <CardDescription>ID: {String(branch.id)}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-2xl border border-border/70 bg-muted/10 p-4">
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Branch instance</p>
                  <p className="mt-2 break-all text-sm font-medium text-foreground">{String(branchInstanceId || '-')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" asChild>
                    <Link to={withServiceInstanceId(basicDetailsPath, serviceInstanceId)}>
                      <FileText className="mr-2 h-4 w-4" />
                      Basic Details
                    </Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedNode(branch); setBranchName(branch.node_name || ''); setIsEditOpen(true) }}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => { setSelectedNode(branch); setIsDeleteOpen(true) }}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <Link to={withServiceInstanceId(`/services/stores/${branch.id}/tags`, serviceInstanceId)}>
                      <Tag className="mr-2 h-4 w-4" />
                      Tags
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </section>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Branch</DialogTitle>
            <DialogDescription>Create a branch under this store with a service type and ownership.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client-branch-name">Branch name</Label>
              <Input id="client-branch-name" value={branchName} onChange={(event) => setBranchName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-branch-service-type">App Type</Label>
              <Select
                value={selectedVariant}
                onValueChange={setSelectedVariant}
                disabled={createMutation.isPending || variantsQuery.isLoading || !(variantsQuery.data ?? []).length}
              >
                <SelectTrigger id="client-branch-service-type" aria-label="App type"><SelectValue placeholder={variantsQuery.isLoading ? 'Loading app types...' : 'Select an app type'} /></SelectTrigger>
                <SelectContent>
                  {(variantsQuery.data ?? []).map((variant) => (
                    <SelectItem key={String(variant.id)} value={variant.service_variant || String(variant.id)}>{variant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-branch-ownership">Branch ownership</Label>
              <Select value={branchOwnership} onValueChange={setBranchOwnership} disabled={createMutation.isPending}>
                <SelectTrigger id="client-branch-ownership" aria-label="Branch ownership">
                  <SelectValue placeholder="Select branch ownership" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OWN_BRANCH_VALUE}>Own Branch</SelectItem>
                  <SelectItem value={VENDOR_BRANCH_VALUE}>Vendor Branch</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Vendor branch means this branch is handled by an external company.</p>
            </div>

            <div className="space-y-2">
              <Label>Branch owner</Label>
              <SearchableSelect
                ariaLabel="Branch owner"
                value={selectedOwnerUserId}
                onValueChange={setSelectedOwnerUserId}
                options={ownerUserOptions}
                placeholder={ownerUsersQuery.isLoading ? 'Loading users...' : 'Choose branch owner'}
                searchPlaceholder="Search users"
                emptyText={ownerUsersQuery.isLoading ? 'Loading users...' : 'No users found.'}
                disabled={ownerUsersQuery.isLoading || ownerUserOptions.length === 0}
              />
              <p className="text-xs text-muted-foreground">
                {branchOwnership === VENDOR_BRANCH_VALUE
                  ? ownerCandidates.length === 0
                    ? 'No vendor users found in the current Organization. Add a vendor user first.'
                    : 'Showing users in the vendors user group.'
                  : 'The current organization or tenant owner is selected by default.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending || !parentServiceInstanceId}>Create Branch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Branch</DialogTitle>
            <DialogDescription>Rename the selected branch.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-client-branch-name">Branch name</Label>
            <Input id="edit-client-branch-name" value={branchName} onChange={(event) => setBranchName(event.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending || !branchName.trim() || !selectedNode}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Branch</DialogTitle>
            <DialogDescription>Remove {selectedNode?.node_name || 'this branch'} from the organization structure.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending || !selectedNode}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}